import { type ComponentProps, useCallback, useMemo } from 'react';
import { type Tabs } from 'expo-router';
import { type TabBarButtonExtraProps } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useTabOptions = () => {
  const { track } = useTelemetry();
  const titles = useRouteTitles();

  // ─── Telemetry ───────────────────────────────────────────────────────

  const trackHomeTabPress = useCallback(() => {
    track('tab-bar.home');
  }, [track]);

  const trackMenuTabPress = useCallback(() => {
    track('tab-bar.menu');
  }, [track]);

  const trackLoyaltyTabPress = useCallback(() => {
    track('tab-bar.loyalty');
  }, [track]);

  const trackReorderTabPress = useCallback(() => {
    track('tab-bar.reorder');
  }, [track]);

  const trackGiftCardsTabPress = useCallback(() => {
    track('tab-bar.gift');
  }, [track]);

  // ─── Per-Tab Options ──────────────────────────────────────────────

  const homeTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.homeTitle,
      tabBarTestID: 'nav-bar.home',
      iconName: 'IconHomeAlternative',
      onPress: trackHomeTabPress,
    }),
    [titles.homeTitle, trackHomeTabPress],
  );

  const menuTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.locationsTitle,
      tabBarTestID: 'nav-bar.order',
      iconName: 'IconMenuBowl',
      onPress: trackMenuTabPress,
    }),
    [titles.locationsTitle, trackMenuTabPress],
  );

  const loyaltyTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.loyaltyTitle,
      tabBarTestID: 'nav-bar.loyalty',
      iconName: 'IconSgRewardBlackOutline',
      onPress: trackLoyaltyTabPress,
    }),
    [titles.loyaltyTitle, trackLoyaltyTabPress],
  );

  const reorderTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.reorderTitle,
      tabBarTestID: 'nav-bar.reorder',
      iconName: 'IconHeartStroke',
      onPress: trackReorderTabPress,
    }),
    [titles.reorderTitle, trackReorderTabPress],
  );

  const giftTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.giftCardsTabTitle,
      tabBarTestID: 'nav-bar.gift-cards',
      iconName: 'IconPromo',
      onPress: trackGiftCardsTabPress,
    }),
    [titles.giftCardsTabTitle, trackGiftCardsTabPress],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    homeTabOptions,
    menuTabOptions,
    loyaltyTabOptions,
    reorderTabOptions,
    giftTabOptions,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type TabScreenOptions = NonNullable<
  ComponentProps<typeof Tabs.Screen>['options']
>;

type TabBarButtonOptions = TabScreenOptions & TabBarButtonExtraProps;
